import React from 'react';
import { 
  MagnifyingGlassIcon, 
  TrashIcon,
  ArrowUturnLeftIcon
} from '@heroicons/react/24/outline';
import { formatDate } from '../utils/dateUtils';

const DeletedRequestsTab = ({ 
  deletedRequests, 
  deletedRequestsLoading, 
  deletedSearch, 
  setDeletedSearch,
  deletedPagination, 
  deletedCurrentPage, 
  setDeletedCurrentPage,
  handleRestoreRequest, 
  restoreLoading,
  getStatusBadge,
  getStatusLabel 
}) => {
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex-1 max-w-lg">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search deleted requests..."
              className="form-input pl-10"
              value={deletedSearch}
              onChange={(e) => setDeletedSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="card overflow-hidden">
        {deletedRequestsLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            <p className="ml-4 text-gray-600">Loading deleted requests...</p>
          </div>
        ) : deletedRequests.length === 0 ? (
          <div className="text-center py-12">
            <TrashIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No deleted requests found.</p>
            <p className="text-gray-400 text-sm mt-2">
              {deletedSearch 
                ? 'Try adjusting your search terms.'
                : 'All deleted requests will appear here.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-red-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Request Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bandwidth
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Deleted Info
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {deletedRequests.map((request) => (
                  <tr key={request.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          Account: {request.account_id}
                        </p>
                        <p className="text-sm text-gray-500">
                          {request.user?.company_name || 'N/A'}
                        </p>
                        <p className="text-sm text-gray-400">
                          Created: {formatDate(request.createdAt)}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <span className="font-medium">{request.current_bandwidth}</span>
                        <span className="text-gray-500 mx-2">→</span>
                        <span className="font-medium text-green-600">{request.requested_bandwidth}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={getStatusBadge(request.status)}>
                        {getStatusLabel ? getStatusLabel(request.status) : request.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <p className="text-gray-900">
                          Deleted: {formatDate(request.deletedAt)}
                        </p>
                        <p className="text-gray-500">
                          By: {request.deleter?.username || 'Unknown'}
                        </p>
                        {request.delete_reason && (
                          <p className="text-gray-600 text-xs mt-1 italic max-w-xs truncate" title={request.delete_reason}>
                            Reason: {request.delete_reason}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleRestoreRequest(request.id)}
                        disabled={restoreLoading}
                        className="text-green-600 hover:text-green-900 disabled:opacity-50 flex items-center"
                        title="Restore Request"
                      >
                        <ArrowUturnLeftIcon className="h-5 w-5 mr-1" />
                        Restore
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {deletedPagination.pages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setDeletedCurrentPage(Math.max(1, deletedCurrentPage - 1))}
                disabled={deletedCurrentPage === 1}
                className="btn-secondary"
              >
                Previous
              </button>
              <button
                onClick={() => setDeletedCurrentPage(Math.min(deletedPagination.pages, deletedCurrentPage + 1))}
                disabled={deletedCurrentPage === deletedPagination.pages}
                className="btn-secondary"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{((deletedCurrentPage - 1) * 10) + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(deletedCurrentPage * 10, deletedPagination.total)}
                  </span> of{' '}
                  <span className="font-medium">{deletedPagination.total}</span> deleted requests
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setDeletedCurrentPage(Math.max(1, deletedCurrentPage - 1))}
                    disabled={deletedCurrentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  {Array.from({ length: Math.min(5, deletedPagination.pages) }, (_, i) => {
                    const page = i + 1;
                    return (
                      <button
                        key={page}
                        onClick={() => setDeletedCurrentPage(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          deletedCurrentPage === page
                            ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setDeletedCurrentPage(Math.min(deletedPagination.pages, deletedCurrentPage + 1))}
                    disabled={deletedCurrentPage === deletedPagination.pages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeletedRequestsTab;